import React, { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import type { IssueLineInput, MakeupRequest, RequirementLine } from '@/services/productionOrders/executionApi'
import type { ExecutionSnapshot } from './executionController'
import {
  ALLOCATION_LABEL,
  ALLOCATION_TONE,
  MAKEUP_STATUS_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  describeError,
  formatQty,
  remainingQty,
} from './executionLabels'

export interface ExecutionActions {
  onRetry: () => void
  onStart: () => void
  onSubmitCompletion: (qtyText: string) => void
  onRequestMakeup: (input: { materialId: string; qtyText: string; reason: string }) => void
  onReserveMakeup: (requestId: string) => void
  onIssue: (lines: IssueLineInput[]) => void
  onClose: () => void
}

type Props = { snapshot: ExecutionSnapshot } & ExecutionActions

const th = 'px-3 py-2 text-left text-xs font-semibold text-[var(--color-text-muted)]'
const thNum = `${th} text-right`
const td = 'px-3 py-2 text-sm align-top'
const tdNum = `${td} text-right tabular-nums`
const inputCls =
  'h-9 w-28 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 text-right text-sm tabular-nums'

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white px-4 py-3">
      <div className="text-xs text-[var(--color-text-muted)]">{label}</div>
      <div className={`mt-1 tabular-nums ${strong ? 'text-2xl font-bold' : 'text-2xl font-semibold'}`}>{value}</div>
    </div>
  )
}

export function getActiveMakeupReservations(
  m: MakeupRequest,
): Array<{ id: string; remaining_qty_base: string }> {
  if (Array.isArray(m.reservations) && m.reservations.length > 0) {
    return m.reservations
      .filter((r) => r.status === 'ACTIVE' && Number(r.remaining_qty_base) > 0)
      .map((r) => ({ id: r.id, remaining_qty_base: r.remaining_qty_base }))
  }
  return []
}

export function buildExecutionIssueLines(
  lines: RequirementLine[],
  makeups: MakeupRequest[],
  issueQty: Record<string, string>,
  isFinal = false,
): IssueLineInput[] {
  if (isFinal) return []
  const out: IssueLineInput[] = []
  for (const l of lines) {
    for (const r of l.reservations) {
      if (r.status !== 'ACTIVE') continue
      const raw = issueQty[r.id] ?? formatQty(Number(r.qty_reserved_base) - Number(r.qty_consumed_base))
      const q = Number(String(raw).replace(',', '.'))
      if (Number.isFinite(q) && q > 0) out.push({ reservation_id: r.id, qty_base: q })
    }
  }
  for (const m of makeups) {
    const activeReservations = getActiveMakeupReservations(m)
    for (const r of activeReservations) {
      const raw =
        issueQty[r.id] ??
        (activeReservations.length === 1 ? issueQty[`mk:${m.id}`] : undefined) ??
        formatQty(r.remaining_qty_base)
      const q = Number(String(raw).replace(',', '.'))
      if (Number.isFinite(q) && q > 0) {
        out.push({ reservation_id: r.id, qty_base: q, makeup_request_id: m.id })
      }
    }
  }
  return out
}

export function ProductionExecutionView({ snapshot, ...actions }: Props): React.ReactElement {
  const { loading, loadError, actionError, notice, order, execution, requirement, makeups, busy } = snapshot
  const [completionQty, setCompletionQty] = useState('')
  const [issueQty, setIssueQty] = useState<Record<string, string>>({})
  const [mkMaterial, setMkMaterial] = useState('')
  const [mkQty, setMkQty] = useState('')
  const [mkReason, setMkReason] = useState('')

  if (loading && !order) return <PageLoading label="Đang tải thông tin sản xuất…" />
  if (loadError) {
    return <PageError message={describeError(loadError.code, loadError.message)} onRetry={actions.onRetry} />
  }
  if (!order || !execution) {
    return <EmptyState title="Không có dữ liệu sản xuất" description="Không tìm thấy lệnh sản xuất này." />
  }

  const status = execution.status
  const planned = Number(order.planned_qty)
  const completed = Number(execution.completed_qty)
  const remaining = remainingQty(planned, execution.completed_qty)
  const pct = planned > 0 ? Math.min(100, Math.round((completed / planned) * 100)) : 0
  const isFinal = status === 'COMPLETED' || status === 'CANCELLED'
  const canStart = status === 'PLANNED' || status === 'READY'
  const lines = requirement?.lines ?? []
  const materialOptions = lines.filter((l) => l.material_id)
  const busyNow = busy !== null

  // Issue rows: one input per ACTIVE reservation; default = server-reported unissued remainder.
  const issueDefault = (reservationId: string, reserved: string, consumed: string) =>
    issueQty[reservationId] ?? formatQty(Number(reserved) - Number(consumed))

  const buildIssueLines = (): IssueLineInput[] =>
    buildExecutionIssueLines(lines, makeups, issueQty, isFinal)

  const matName = (id: string) => lines.find((l) => l.material_id === id)?.material_name ?? id

  return (
    <div className="space-y-5">
      {actionError ? (
        <div
          role="alert"
          className="rounded-[var(--radius-md)] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {describeError(actionError.code, actionError.message)}
          <span className="ml-2 text-xs opacity-70">({actionError.code})</span>
        </div>
      ) : null}
      {notice ? (
        <div role="status" className="rounded-[var(--radius-md)] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {notice}
        </div>
      ) : null}

      {/* Tổng quan */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-base font-semibold">
            {order.item_code} — {order.product_name}
          </h2>
          <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{STATUS_LABEL[status] ?? status}</Badge>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat label="Số lượng kế hoạch" value={formatQty(planned)} />
          <Stat label="Đã hoàn thành" value={formatQty(completed)} />
          <Stat label="Còn lại" value={formatQty(remaining)} strong />
        </div>
        <div>
          <div className="mb-1 flex justify-between text-xs text-[var(--color-text-muted)]">
            <span>Tiến độ hoàn thành</span>
            <span data-testid="progress-pct">{pct}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded bg-slate-200" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full bg-[var(--color-accent)]" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {!isFinal ? (
          <div className="flex flex-wrap gap-2">
            {canStart ? (
              <Button disabled={busyNow} onClick={actions.onStart}>
                {busy === 'start' ? 'Đang bắt đầu…' : 'Bắt đầu sản xuất'}
              </Button>
            ) : null}
            <Button variant="secondary" disabled={busyNow} onClick={actions.onClose}>
              {busy === 'close' ? 'Đang đóng lệnh…' : 'Đóng lệnh sản xuất'}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">
            Lệnh sản xuất đã {status === 'COMPLETED' ? 'đóng — hoàn thành' : 'hủy'}. Không thể thao tác thêm.
          </p>
        )}
      </section>

      {/* Ghi nhận hoàn thành */}
      {!isFinal ? (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Ghi nhận số lượng hoàn thành</h3>
          {status === 'IN_PROGRESS' ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label="Số lượng hoàn thành thêm"
                inputMode="decimal"
                className={inputCls}
                value={completionQty}
                disabled={busyNow}
                onChange={(e) => setCompletionQty(e.target.value)}
              />
              <Button
                disabled={busyNow}
                onClick={() => {
                  actions.onSubmitCompletion(completionQty)
                }}
              >
                {busy === 'completion' ? 'Đang ghi nhận…' : 'Ghi nhận hoàn thành'}
              </Button>
              <span className="text-xs text-[var(--color-text-muted)]">Nhập số lượng hoàn thành thêm lần này.</span>
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">Bấm “Bắt đầu sản xuất” trước khi ghi nhận hoàn thành.</p>
          )}
        </section>
      ) : null}

      {/* Vật tư */}
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Vật tư sản xuất</h3>
          {!isFinal && (lines.length > 0 || makeups.length > 0) ? (
            <Button disabled={busyNow} onClick={() => actions.onIssue(buildIssueLines())}>
              {busy === 'issue' ? 'Đang xuất vật tư…' : 'Xuất vật tư'}
            </Button>
          ) : null}
        </div>
        {lines.length === 0 ? (
          <EmptyState
            title="Chưa có nhu cầu vật tư"
            description="Lệnh này chưa tạo nhu cầu vật tư sản xuất."
          />
        ) : (
          <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white">
            <table className="w-full min-w-[820px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className={th}>STT</th>
                  <th className={th}>Vật tư</th>
                  <th className={th}>ĐVT</th>
                  <th className={thNum}>Cần</th>
                  <th className={thNum}>Đã giữ hàng</th>
                  <th className={thNum}>Đã xuất</th>
                  <th className={thNum}>Còn thiếu</th>
                  <th className={thNum}>Xuất lần này</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const active = l.reservations.filter((r) => r.status === 'ACTIVE')
                  return (
                    <tr key={l.requirement_line_id} className="border-t border-[var(--color-border)]">
                      <td className={td}>{i + 1}</td>
                      <td className={td}>
                        <div className="font-medium">{l.material_name}</div>
                      </td>
                      <td className={td}>{l.uom}</td>
                      <td className={tdNum}>{formatQty(l.required)}</td>
                      <td className={tdNum}>{formatQty(l.reserved_total)}</td>
                      <td className={tdNum}>{formatQty(l.consumed)}</td>
                      <td className={`${tdNum} ${l.shortage > 0 ? 'font-semibold text-[var(--color-danger)]' : ''}`}>
                        {formatQty(l.shortage)}
                      </td>
                      <td className={tdNum}>
                        {isFinal || active.length === 0 ? (
                          <span className="text-xs text-[var(--color-text-muted)]">—</span>
                        ) : (
                          <div className="flex flex-col items-end gap-1">
                            {active.map((r) => (
                              <input
                                key={r.id}
                                aria-label={`Xuất ${l.material_name}`}
                                inputMode="decimal"
                                className={inputCls}
                                disabled={busyNow}
                                value={issueDefault(r.id, r.qty_reserved_base, r.qty_consumed_base)}
                                onChange={(e) => setIssueQty((s) => ({ ...s, [r.id]: e.target.value }))}
                              />
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Bổ sung vật tư (hao hụt / làm bù) */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">Bổ sung vật tư (hao hụt / làm bù)</h3>
        {!isFinal ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs text-[var(--color-text-muted)]">
              Vật tư
              <select
                aria-label="Vật tư cần bổ sung"
                className="h-9 min-w-[220px] rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-text)]"
                value={mkMaterial}
                disabled={busyNow}
                onChange={(e) => setMkMaterial(e.target.value)}
              >
                <option value="">— Chọn vật tư —</option>
                {materialOptions.map((l) => (
                  <option key={l.requirement_line_id} value={l.material_id ?? ''}>
                    {l.material_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-[var(--color-text-muted)]">
              Số lượng
              <input
                aria-label="Số lượng bổ sung"
                inputMode="decimal"
                className={inputCls}
                value={mkQty}
                disabled={busyNow}
                onChange={(e) => setMkQty(e.target.value)}
              />
            </label>
            <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-xs text-[var(--color-text-muted)]">
              Lý do
              <input
                aria-label="Lý do bổ sung"
                className="h-9 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-text)]"
                value={mkReason}
                disabled={busyNow}
                onChange={(e) => setMkReason(e.target.value)}
              />
            </label>
            <Button
              disabled={busyNow}
              onClick={() => actions.onRequestMakeup({ materialId: mkMaterial, qtyText: mkQty, reason: mkReason })}
            >
              {busy === 'makeup' ? 'Đang gửi yêu cầu…' : 'Yêu cầu bổ sung'}
            </Button>
          </div>
        ) : null}
        <p className="text-xs text-[var(--color-text-muted)]">
          Danh sách yêu cầu bổ sung vật tư đã lưu trên hệ thống.
        </p>
        {makeups.length === 0 ? (
          <EmptyState title="Chưa có yêu cầu bổ sung" />
        ) : (
          <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white">
            <table className="w-full min-w-[900px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className={th}>STT</th>
                  <th className={th}>Vật tư</th>
                  <th className={th}>Lý do</th>
                  <th className={thNum}>Yêu cầu</th>
                  <th className={thNum}>Đã giữ hàng</th>
                  <th className={thNum}>Còn thiếu</th>
                  <th className={thNum}>Đã xuất</th>
                  <th className={th}>Tình trạng</th>
                  <th className={thNum}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {makeups.map((m: MakeupRequest, i) => {
                  const closed = m.status === 'ISSUED' || m.status === 'CANCELLED'
                  const activeReservations = getActiveMakeupReservations(m)
                  const canIssue = !isFinal && !closed && activeReservations.length > 0
                  const isIssued = m.status === 'ISSUED'
                  return (
                    <tr key={m.id} className="border-t border-[var(--color-border)]">
                      <td className={td}>{i + 1}</td>
                      <td className={td}>{matName(m.material_id)}</td>
                      <td className={td}>{m.reason}</td>
                      <td className={tdNum}>{formatQty(m.qty_base)}</td>
                      <td className={tdNum}>{formatQty(m.reserved_qty_base)}</td>
                      <td className={`${tdNum} ${Number(m.shortage_qty_base) > 0 ? 'font-semibold text-[var(--color-danger)]' : ''}`}>
                        {formatQty(m.shortage_qty_base)}
                      </td>
                      <td className={tdNum}>{formatQty(m.consumed_qty_base)}</td>
                      <td className={td}>
                        <Badge tone={isIssued ? 'neutral' : (ALLOCATION_TONE[m.allocation_status] ?? 'neutral')}>
                          {isIssued ? (MAKEUP_STATUS_LABEL[m.status] ?? 'Đã xuất đủ') : (ALLOCATION_LABEL[m.allocation_status] ?? m.allocation_status)}
                        </Badge>
                      </td>
                      <td className={tdNum}>
                        <div className="flex flex-col items-end gap-1">
                          {!closed && !isFinal && Number(m.shortage_qty_base) > 0 ? (
                            <Button size="sm" variant="secondary" disabled={busyNow} onClick={() => actions.onReserveMakeup(m.id)}>
                              Giữ hàng khả dụng
                            </Button>
                          ) : null}
                          {canIssue ? (
                            activeReservations.map((r) => (
                              <input
                                key={r.id}
                                aria-label="Xuất bổ sung"
                                data-testid={`issue-mk-${r.id}`}
                                inputMode="decimal"
                                className={inputCls}
                                disabled={busyNow}
                                value={
                                  issueQty[r.id] ??
                                  (activeReservations.length === 1 ? issueQty[`mk:${m.id}`] : undefined) ??
                                  formatQty(r.remaining_qty_base)
                                }
                                onChange={(e) => setIssueQty((s) => ({ ...s, [r.id]: e.target.value }))}
                              />
                            ))
                          ) : null}
                          {closed ? <span className="text-xs text-[var(--color-text-muted)]">—</span> : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-[var(--color-text-muted)]">
          Số lượng bổ sung được xuất cùng nút “Xuất vật tư” phía trên.
        </p>
      </section>
    </div>
  )
}
